import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Navbar, Footer } from "../../components/Layout";
import { toast, Toaster } from "sonner";
import { ArrowLeft, Plus, Trash2, Save, ChevronUp, ChevronDown } from "lucide-react";

export default function ActivityTypesAdmin() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState({}); // id -> {name, description, sort_order}
  const [newItem, setNewItem] = useState({ name: "", description: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const r = await api.get("/admin/activity-types");
    setItems(r.data);
    setEditing(r.data.reduce((acc, x) => ({ ...acc, [x.id]: { name: x.name, description: x.description || "", sort_order: x.sort_order } }), {}));
  };

  useEffect(() => { load().catch(() => toast.error("Échec de chargement")); }, []);

  const setField = (id, k, v) => setEditing({ ...editing, [id]: { ...editing[id], [k]: v } });

  const save = async (id) => {
    setSaving(true);
    try {
      await api.put(`/admin/activity-types/${id}`, editing[id]);
      toast.success("Mis à jour");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Échec");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!confirm("Supprimer ce type d'activité ?")) return;
    await api.delete(`/admin/activity-types/${id}`);
    toast.success("Supprimé");
    load();
  };

  const create = async () => {
    if (!newItem.name.trim()) return;
    const maxOrder = items.reduce((m, i) => Math.max(m, i.sort_order || 0), 0);
    await api.post("/admin/activity-types", { ...newItem, sort_order: maxOrder + 1 });
    toast.success("Créé");
    setNewItem({ name: "", description: "" });
    load();
  };

  const move = async (id, dir) => {
    const idx = items.findIndex(x => x.id === id);
    const other = items[idx + dir];
    if (!other) return;
    await Promise.all([
      api.put(`/admin/activity-types/${id}`, { sort_order: other.sort_order }),
      api.put(`/admin/activity-types/${other.id}`, { sort_order: items[idx].sort_order }),
    ]);
    load();
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--bg-default)" }}>
      <Navbar />
      <Toaster richColors position="top-right" />
      <section className="max-w-4xl mx-auto px-6 md:px-8 py-12">
        <button onClick={() => navigate("/admin")} className="text-xs font-bold uppercase tracking-widest mb-6 flex items-center gap-2" style={{ color: "var(--text-secondary)" }} data-testid="back-to-dashboard">
          <ArrowLeft className="w-3 h-3" /> Retour au tableau de bord
        </button>
        <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight mb-2">Types d'activité</h1>
        <p className="text-sm mb-8" style={{ color: "var(--text-secondary)" }}>Liste proposée aux visiteurs dans le formulaire de réservation (étape 1).</p>

        <div className="bg-[color:var(--bg-surface)] border p-6 mb-8 space-y-3" style={{ borderColor: "var(--border-default)" }} data-testid="activity-new">
          <h2 className="font-display font-bold text-lg">Ajouter un type d'activité</h2>
          <div className="grid md:grid-cols-[1fr_2fr_auto] gap-3">
            <input value={newItem.name} onChange={e => setNewItem({ ...newItem, name: e.target.value })} placeholder="Nom *" className="input" data-testid="activity-new-name" />
            <input value={newItem.description} onChange={e => setNewItem({ ...newItem, description: e.target.value })} placeholder="Description" className="input" />
            <button onClick={create} disabled={!newItem.name.trim()} data-testid="activity-create-btn"
              className="btn-brutal btn-brutal-solar px-5 py-3 font-bold uppercase tracking-wider text-sm flex items-center gap-2 disabled:opacity-50">
              <Plus className="w-4 h-4" /> Créer
            </button>
          </div>
        </div>

        <div className="space-y-3">
          {items.map((it, idx) => (
            <div key={it.id} className="bg-[color:var(--bg-surface)] border p-5 grid md:grid-cols-[auto_1fr_2fr_auto_auto] gap-3 items-center" style={{ borderColor: "var(--border-default)" }} data-testid={`activity-row-${it.id}`}>
              <div className="flex flex-col gap-1">
                <button onClick={() => move(it.id, -1)} disabled={idx === 0} className="opacity-60 hover:opacity-100 disabled:opacity-20"><ChevronUp className="w-4 h-4"/></button>
                <button onClick={() => move(it.id, 1)} disabled={idx === items.length - 1} className="opacity-60 hover:opacity-100 disabled:opacity-20"><ChevronDown className="w-4 h-4"/></button>
              </div>
              <input value={editing[it.id]?.name || ""} onChange={e => setField(it.id, "name", e.target.value)} className="input" />
              <input value={editing[it.id]?.description || ""} onChange={e => setField(it.id, "description", e.target.value)} className="input" placeholder="Description (optionnel)" />
              <button onClick={() => save(it.id)} disabled={saving} className="btn-brutal bg-[color:var(--solar)] border border-slate-900 px-4 py-2 text-xs font-bold uppercase tracking-wider flex items-center gap-1 disabled:opacity-50">
                <Save className="w-3 h-3" /> Save
              </button>
              <button onClick={() => remove(it.id)} className="text-red-600 hover:bg-red-50 p-2"><Trash2 className="w-4 h-4"/></button>
            </div>
          ))}
          {items.length === 0 && <div className="text-center py-12 text-sm opacity-60">Aucun type d'activité.</div>}
        </div>
      </section>
      <Footer />
    </div>
  );
}
